import { useForm } from 'antd/lib/form/Form';
import { usePaymentTopUp } from 'components/Pages/Wallet/components/WalletTransaction/service';
import {
  BALANCE_MODAL_TYPE,
  DEFAULT_BALANCE_PAYMENT_METHOD_OPTIONS,
  PAYMENT_TYPE,
} from 'constants/common';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { selectTransactionQuery } from 'states/transaction/selectors';
import { showModalError, showModalSuccessfulScreen } from 'utilities/modal';
import useBalanceSdk from './useBalanceSdk';
import { useGetListPaymentMethod, useGetTransactions } from './usePayment';
import useProfile from './useProfile';
import { t } from 'utilities/lang';

const useBalanceCheckout = (idModal, options, disableInit = false) => {
  const { balanceWidget } = useBalanceSdk();
  const [visible, setVisible] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [paymentType, setPaymentType] = useState(PAYMENT_TYPE.CREDIT_CARD);
  const [form] = useForm();
  const { data: userInfo } = useProfile();

  const intl = useIntl();

  const transactionInfo = useRef({
    transactionId: null,
    amount: null,
  });

  const onChangePaymentType = useCallback((value) => {
    setPaymentType(value);
  }, []);

  useEffect(() => {
    if (balanceWidget?.balanceSDK && !disableInit) {
      const { balanceSDK } = balanceWidget;
      const balanceProps = {
        ...options,
        allowedPaymentMethods:
          paymentType === PAYMENT_TYPE.TERM
            ? ['payWithTerms']
            : paymentType === PAYMENT_TYPE.CREDIT_CARD
            ? ['creditCard']
            : paymentType === PAYMENT_TYPE.BANK
            ? ['bank']
            : paymentType === PAYMENT_TYPE.WIRE_TRANSFER
            ? ['invoice', 'wire']
            : DEFAULT_BALANCE_PAYMENT_METHOD_OPTIONS.allowedPaymentMethods,
      };

      balanceSDK.checkout.init(balanceProps);
    }
  }, [options, balanceWidget, paymentType]);

  const onOpenModal = (token) => {
    try {
      setVisible(true);
      const { balanceSDK } = balanceWidget;

      balanceSDK?.checkout?.render(token, idModal);
    } catch (error) {
      showModalError({ title: t(`error.message.${error.code}`) || error.message });
      setVisible(false);
      options.onClose();
    }
  };

  const onCloseModal = useCallback(() => {
    try {
      const { balanceSDK } = balanceWidget;
      balanceSDK.checkout.destroy();
      balanceSDK.checkout.sdkProps.onClose();
    } catch (error) {
      showModalError({ title: t(`error.message.${error.code}`) || error.message });
      options.onClose();
    }
  }, [options, balanceWidget]);

  const filters = useSelector(selectTransactionQuery);
  const { refetch: refetchTransactions } = useGetTransactions(filters);
  const { refetch: refetchProfile } = useProfile();
  const PaymentTopup = usePaymentTopUp();
  const { refetch: refetchPaymentMethods } = useGetListPaymentMethod();
  const topupPayment = () => {
    PaymentTopup.mutate(
      {
        transactionId: transactionInfo.current.transactionId,
        amount: Number(transactionInfo.current.amount),
        type: paymentType,
      },
      {
        onSuccess: () => {
          setCheckoutSuccess(false);
          refetchTransactions();
          refetchProfile();
          form.resetFields();
          setPaymentType(PAYMENT_TYPE.CREDIT_CARD);
          onCloseModal();
          refetchPaymentMethods();
        },
        onError(err) {
          showModalError({ content: t(`error.message.${err?.code}`) || err?.message });
          setCheckoutSuccess(false);
        },
      }
    );
  };

  const resetBalanceModal = () => {
    setVisible(false);
    setCheckoutSuccess(false);
    setPaymentType(PAYMENT_TYPE.CREDIT_CARD);
  };

  useEffect(() => {
    if (
      idModal === `#${BALANCE_MODAL_TYPE.BALANCE_CHECKOUT_TOPUP}` &&
      checkoutSuccess
    ) {
      paymentType === PAYMENT_TYPE.WIRE_TRANSFER &&
        showModalSuccessfulScreen({
          title: t('balance_checkout.wire_transfer.title'),
          description: t(
            'balance_checkout.wire_transfer.description',
            {
              adminName: userInfo?.accountingAdminDisplayName,
              adminEmail: userInfo?.accountingAdminEmail,
            }
          ),
          primaryButton: t('common.ok'),
        });
      topupPayment();
    }
  }, [checkoutSuccess]);

  return {
    onOpenModal,
    checkoutSuccess,
    setCheckoutSuccess,
    transactionInfo,
    visible,
    setVisible,
    paymentType,
    onChangePaymentType,
    form,
    onCloseModal,
    resetBalanceModal,
    idModal,
    balanceWidget,
  };
};

export default useBalanceCheckout;
